import { Dispatch, SetStateAction, useEffect, useRef } from "react";

/**
 * Keeps an edit session out of the pending-task list.
 *
 * Opening a document to edit it — or an offer to invoice it — borrows the task
 * machinery to hold the lines being worked on, but that draft is not a task
 * anybody should come back to: the document already exists. The list lives in a
 * page-level provider, though, so the borrowed task outlived the modal, and the
 * next ordinary draft saved to storage carried it along.
 *
 * The edit task is dropped when the session ends — saved, cancelled or simply
 * closed. Tasks that were already there, or that arrive from a shared draft
 * poll while the modal is open, are left alone.
 */
export const useEphemeralEditTask = <T extends { id: string }>({
	editing,
	tasks,
	setTasks,
}: {
	/** True while the modal is working on an existing document. */
	editing: boolean;
	tasks: T[];
	setTasks: Dispatch<SetStateAction<T[]>>;
}) => {
	const tasksRef = useRef(tasks);
	tasksRef.current = tasks;
	const initialIdsRef = useRef<Set<string>>(new Set());
	const ephemeralIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!editing) return;
		// Snapshot before the modal's opening effect adds its active edit task.
		initialIdsRef.current = new Set(tasksRef.current.map((task) => task.id));
		ephemeralIdRef.current = null;
		return () => {
			const ephemeralId = ephemeralIdRef.current;
			if (ephemeralId) {
				setTasks((prev) => prev.filter((task) => task.id !== ephemeralId));
			}
		};
	}, [editing, setTasks]);

	useEffect(() => {
		if (!editing) return;
		// Modals create their edit task as the active row. Remote drafts merged
		// during the same session are inactive; never infer ownership from every
		// id that was absent at mount.
		const activeAddedTask = [...tasks].reverse().find(
			(task) => (task as T & { active?: boolean }).active
				&& !initialIdsRef.current.has(task.id),
		);
		if (activeAddedTask) ephemeralIdRef.current = activeAddedTask.id;
	}, [editing, tasks]);
};

export default useEphemeralEditTask;
